/**
 * @fileoverview Module for `src/page/Footer.js`.
 */

import { Link } from 'react-router-dom';

import { message } from '../lib/deprecated_i18n';
import { useAuth } from '../contexts/AuthContext';

import '../scss/lib/_buttons.scss';
import '../scss/lib/_typography.scss';
import '../scss/page/_footer.scss';

const helpUrl = 'https://help.votefwd.org';

/**
 * Global site footer with navigation links, donate CTA, and social links.
 *
 * @returns {JSX.Element} Footer layout.
 */
function Footer() {
  const auth = useAuth();

  return (
    <footer className='footer'>
      <div className='footer-content'>
        <div className='footer-grid'>
          { /* Left Column - Logo, description, donate button */ }
          <div className='footer-left'>
            <Link className='logo' to='/'>
              <img alt='Vote Forward' src='/images/vf-logo-2026-light.svg' />
            </Link>
            <div className='about'>
              <p>
                Our small team is hard at work developing new campaigns,
                analyzing data, and working to improve the letter writing
                experience for volunteers like you! If you can, consider
                making a contribution to help keep our work going.
              </p>
            </div>
            <div className='donate-button-container'>
              <a
                className='btn'
                href='https://secure.actblue.com/donate/vote-forward-c4?refcode=webfooter'
                rel='noopener noreferrer'
                target='_blank'
              >
                Support this work
              </a>
            </div>
          </div>

          { /* Right Column - Link groups, social icons, copyright */ }
          <div className='footer-right'>
            <div className='link-group'>
              <h6>About us</h6>
              <div className='link-list'>
                <a href='https://votefwd.org/about' rel='noopener noreferrer' target='_blank'>Mission</a>
                <span className='separator' />
                <a href='https://votefwd.org/impact' rel='noopener noreferrer' target='_blank'>Impact</a>
                <span className='separator' />
                <a href='https://votefwd.org/blog' rel='noopener noreferrer' target='_blank'>Blog</a>
                <span className='separator' />
                <a href='https://votefwd.org/open-positions' rel='noopener noreferrer' target='_blank'>Careers</a>
                <span className='separator' />
                <a href='https://votefwd.org/contact' rel='noopener noreferrer' target='_blank'>Contact</a>
              </div>
            </div>
            <div className='link-group'>
              <h6>Resources</h6>
              <div className='link-list'>
                <a href='https://votefwd.org/partners' rel='noopener noreferrer' target='_blank'>Partners</a>
                <span className='separator' />
                <a href='https://votefwd.org/events' rel='noopener noreferrer' target='_blank'>Events</a>
                <span className='separator' />
                <a href='https://votefwd.org/instructions' rel='noopener noreferrer' target='_blank'>Instructions</a>
                <span className='separator' />
                <Link rel='noopener noreferrer' target='_blank' to='/letter-kits'>Letter Kits</Link>
                <span className='separator' />
                <a href='https://votefwd.org/faq' rel='noopener noreferrer' target='_blank'>FAQs</a>
              </div>
            </div>
            <div className='link-group'>
              <h6>Settings</h6>
              <div className='link-list'>
                <Link to={ auth.isAuthenticated() ? '/profile' : '/update-preferences' }>
                  Profile
                </Link>
                <span className='separator' />
                <Link to='/opt-out'>Letter Opt Out</Link>
                <span className='separator' />
                <a href={ helpUrl } rel='noopener noreferrer' target='_blank'>Help</a>
              </div>
            </div>

            { /* Social Icons */ }
            <div className='social-icons'>
              <a aria-label='Vote Forward on Instagram' href='https://www.instagram.com/votefwd/' rel='noopener noreferrer' target='_blank'>
                <i className='fa fa-instagram' />
              </a>
              <a aria-label='Vote Forward on X (Twitter)' href='https://twitter.com/votefwd' rel='noopener noreferrer' target='_blank'>
                <i className='fa fa-twitter' />
              </a>
              <a aria-label='Vote Forward on Facebook' href='https://fb.me/votefwd' rel='noopener noreferrer' target='_blank'>
                <i className='fa fa-facebook' />
              </a>
              <a aria-label='Vote Forward on Bluesky' href='https://blueskydirectory.com/profiles/votefwd.bsky.social' rel='noopener noreferrer' target='_blank'>
                <i className='fa fa-bluesky' />
              </a>
            </div>

            { /* Copyright */ }
            <p className='copyright'>
              { message('footer.copyright') }
              <Link to='/privacy-policy'>Privacy</Link>
              /
              <Link to='/terms-of-use'>Terms</Link>
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
}

export default Footer;
